import { LogOut, FileText, Table2, Presentation, Sparkles, Users2, Video, MailQuestion } from 'lucide-react';
import type { AuthUser } from '../api/auth';
import { signOut } from '../api/auth';
import type { OfficeHost } from '../office/bootstrap';
import { blueprintIdForHost, isHostSupported } from '../lib/hostAgentMap';
import { useHostAgentId } from '../hooks/useHostAgentId';
import { ChatPanel } from './ChatPanel';

interface Props {
  brand: string;
  host: OfficeHost;
  user: AuthUser;
  onSignedOut: () => void;
}

export function AppShell({ brand, host, user, onSignedOut }: Props) {
  const blueprintId = blueprintIdForHost(host);
  const supported = isHostSupported(host);
  const resolver = useHostAgentId(supported ? blueprintId : undefined);

  async function doSignOut() {
    await signOut();
    onSignedOut();
  }

  return (
    <div className="flex h-full flex-col bg-white">
      <header className="flex items-center justify-between gap-2 border-b border-ink-100 bg-white px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <HostIcon host={host} />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-ink-900">{brand}</div>
            <div className="truncate text-[11px] text-ink-500">
              {hostLabel(host)} · {user.email || user.username}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={doSignOut}
          className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100/60 hover:text-ink-900"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 min-h-0">
        {resolver.status === 'ready' && resolver.agentId ? (
          <ChatPanel host={host} agentId={resolver.agentId} />
        ) : !supported || resolver.status === 'error' ? (
          <ChatPanel host={host} />
        ) : (
          <div className="flex h-full items-center justify-center text-ink-500">
            <Sparkles className="mr-2 h-4 w-4 animate-pulse" />
            Loading {host} agent…
          </div>
        )}
      </div>
    </div>
  );
}

function HostIcon({ host }: { host: OfficeHost }) {
  const cls = 'h-7 w-7 rounded-lg p-1.5 text-white shadow-soft';
  switch (host) {
    case 'Word':       return <FileText     className={`${cls} bg-[#2b579a]`} />;
    case 'Excel':      return <Table2       className={`${cls} bg-[#217346]`} />;
    case 'PowerPoint': return <Presentation className={`${cls} bg-[#b7472a]`} />;
    case 'Outlook':    return <MailQuestion className={`${cls} bg-[#0078d4]`} />;
    case 'Teams':      return <Users2       className={`${cls} bg-[#4b53bc]`} />;
    case 'Meet':       return <Video        className={`${cls} bg-[#00897b]`} />;
    default:           return <Sparkles     className={`${cls} bg-brand-500`} />;
  }
}

function hostLabel(host: OfficeHost): string {
  switch (host) {
    case 'Word':
    case 'Excel':
    case 'PowerPoint':
    case 'Outlook':    return `for ${host}`;
    case 'Teams':      return 'in Microsoft Teams';
    case 'Meet':       return 'in Google Meet';
    default:           return 'Standalone preview';
  }
}
